import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useRelayEnvironment } from 'react-relay';

import useBatchedFetchErrorState from 'src/hooks/useBatchedFetchErrorState';
import logger from 'src/util/logger';
import { measureSince, nowMs } from 'src/util/perfMarks';

import { getPortfolioDataInBatches } from '../helpers/dataBatching';
import {
  addSegmentToCache,
  buildCacheAggregationKey,
  clearCache,
  computeCacheMetadata,
  extractDataFromCache,
  getPortfolioDataFromCache,
  getSegmentDataFromCache,
  lookupCache,
} from '../helpers/dataCaching';
import { PortfolioShowLoadingActions } from '../reducer/constants';

/**
 * Custom hook that encapsulates all portfolio data fetching.
 * Two-effect pattern:
 *   Initial-load effect (portfolioId) — full load: meters, rules, energy, trade summaries.
 *   Date-range effect (initialLoadComplete + timeRange) — date-range change: checks the
 *     read-through cache in dataCaching.js. On cache miss, runs the full 5-stage
 *     pipeline (including meters) via getPortfolioDataInBatches.
 * @param {object} params
 * @param {string} params.portfolioId - Portfolio UUID
 * @param {object} params.timeRange - { start, finish } Luxon DateTimes
 * @param {string} params.timezone - Timezone string
 * @param {object} params.initialData - Static portfolio data (from Relay fragment)
 * @param {string} params.historianAggregation - Aggregation for energy data
 * @param {string} params.tradeAggregation - Aggregation for trade data
 * @param {Function} [params.stateDispatchFunc] - Reducer dispatch for loading state
 * @returns {object} Object containing:
 *   - combinedData: {object} Combined portfolio data from GraphQL responses
 *   - cancelFunc: {Function} Cancels the in-flight fetch
 *   - clearError: {Function} Clears the error state without retrying
 *   - error: {Error|null} The last fetch error
 *   - handleRetry: {Function} Replays the full pipeline and resynchronises loading state
 *   - isRetrying: {boolean} True while a replay fetch is in flight
 *   - queryType: {string|null} The query stage that produced the error
 *   - getRecoveredSpanFromCache: {Function} Pure lookup — returns {start, finish, data}
 *     from the last successful load's cached segment, or null on a miss. No state mutations.
 *   - applyRecoveredCacheData: {Function} Applies a recovered span: sets combinedData,
 *     clears the error, and resolves any in-flight retry indicator.
 */
const usePortfolioData = ({
  portfolioId,
  timeRange,
  timezone,
  initialData,
  historianAggregation,
  tradeAggregation,
  stateDispatchFunc,
}) => {
  const environment = useRelayEnvironment();

  const [combinedData, setCombinedData] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Synchronous mirror of initialLoadComplete so the initial-load effect can gate
  // the date-range effect within the same render pass (setState is async/batched).
  const initialLoadCompleteRef = useRef(false);
  // When the data now in combinedData settled, keyed by that data object so
  // PortfolioShow can time settlement to commit without trusting a stale value.
  const settlementRef = useRef({ data: null, settledAt: null });

  const isFetchingData = useRef(false);
  const cancelTokenRef = useRef({ current: false });
  const skipTimeRangeRef = useRef(null);
  const timeRangeRef = useRef(timeRange);
  timeRangeRef.current = timeRange;

  // Records the timeRange that produced the current error. The date-range effect
  // uses it to discard a stale error only when the span genuinely changes, while
  // leaving a just-surfaced error intact on the extra same-span re-run that fires
  // when onError flips initialLoadComplete (a date-range effect dependency).
  const errorSpanRef = useRef(null);

  // Keep refs for values used inside the initial-load effect callbacks so they always
  // reflect the latest props. The initial-load effect only re-runs on portfolioId
  // change; these refs let the onDataReceived callback detect when
  // other params went stale while the request was in flight.
  const timezoneRef = useRef(timezone);
  timezoneRef.current = timezone;
  const historianAggregationRef = useRef(historianAggregation);
  historianAggregationRef.current = historianAggregation;
  const tradeAggregationRef = useRef(tradeAggregation);
  tradeAggregationRef.current = tradeAggregation;

  // Tracks the timeRange that the initial-load effect loaded, so the date-range effect
  // can skip the duplicate fetch when initialLoadComplete first becomes true.
  const initialTimeRangeRef = useRef(null);

  // Records the last timeRange and cache key for which data was successfully loaded.
  // Used by getRecoveredCacheData to restore cached data after a fetch error.
  const lastSuccessfulLoadRef = useRef(null);

  // Keep a stable ref to the latest stateDispatchFunc so onErrorDispatch can
  // remain referentially stable (the error-state hook memoises on it).
  const stateDispatchFuncRef = useRef(stateDispatchFunc);
  stateDispatchFuncRef.current = stateDispatchFunc;

  // On an async fetch error, drive the loading reducer into CANCELLED so the
  // overlay is hidden while the error modal is shown. Matches the pattern used
  // by PropertyShow and TradeList.
  const onErrorDispatch = useCallback(() => {
    if (stateDispatchFuncRef.current) {
      stateDispatchFuncRef.current({ type: PortfolioShowLoadingActions.CANCEL_REQUEST });
    }
  }, []);

  const {
    error,
    isRetrying,
    retryNonce,
    queryType,
    clearError,
    resetError,
    resolveRetry,
    handleFetchError,
    handleRetry: triggerRetry,
  } = useBatchedFetchErrorState({ onErrorDispatch });

  // Retry replays the entire pipeline by bumping retryNonce (via the shared hook)
  // and immediately clearing local data so the UI transitions out of the
  // stale-data view while the new fetch is in flight.
  //
  // START_REQUEST is dispatched synchronously here (mirroring handleTimespanUpdate)
  // so the batched re-render triggered by this click already reports a loading
  // state. Without it, retryNonce only re-runs the initial-load effect in the
  // effect phase, leaving the reducer in its terminal ERROR/CANCELLED state for
  // one render — long enough to flash the chart's stale "no data" view before the
  // overlay appears. The reducer's terminal-state guard explicitly permits
  // START_REQUEST, so this transition is valid from both ERROR and CANCELLED.
  const handleRetry = useCallback(() => {
    setCombinedData(null);
    if (stateDispatchFuncRef.current) {
      stateDispatchFuncRef.current({ type: PortfolioShowLoadingActions.START_REQUEST });
    }
    triggerRetry();
  }, [triggerRetry]);

  // Clear cache on unmount
  useEffect(() => () => {
    clearCache();
  }, []);

  // ── Initial-load effect: Full initial load ────────────────────
  useEffect(() => {
    if (!portfolioId) {
      logger.error('No portfolio ID found for portfolio data fetching');
      return undefined;
    }

    logger.log('Effect 1: full initial load for portfolio:', portfolioId);

    // Reset for the new portfolio so the date-range effect does not run in parallel
    // with the initial-load effect when portfolioId changes without unmounting.
    // The ref is cleared synchronously so the date-range effect sees it in the same
    // render pass; the state update triggers a re-render for the dep array.
    initialLoadCompleteRef.current = false;
    setInitialLoadComplete(false);
    setCombinedData(null);
    initialTimeRangeRef.current = null;
    // Discard any stale error from a previous portfolio (or retry) before the
    // new load resolves, so a portfolioId change while errored transitions the
    // UI out of the error fallback rather than flashing the prior error modal.
    resetError();
    errorSpanRef.current = null;

    if (stateDispatchFunc) {
      stateDispatchFunc({ type: PortfolioShowLoadingActions.START_REQUEST });
    }

    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    getPortfolioDataInBatches({
      portfolioId,
      timeRange,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      dispatchFunc: stateDispatchFunc,
      onDataReceived: (mergedData, _tradeSummaryBatches, _meters, settledAt) => {
        const receivedAt = settledAt ?? nowMs();
        logger.log('Received merged portfolio data');
        initialTimeRangeRef.current = timeRange;
        // Seed the cache so the date-range effect can serve subsets from it
        lookupCache(portfolioId);
        const cacheKey = buildCacheAggregationKey(
          historianAggregation,
          tradeAggregation,
          timezone,
        );
        const cacheInsertStart = nowMs();
        addSegmentToCache(timeRange, mergedData, cacheKey);
        if (cacheInsertStart !== null) {
          measureSince('portfolio-show:cacheInsert:initial', cacheInsertStart);
        }

        // If the user changed timeRange, timezone, or aggregation while the
        // request was in flight, the fetched data is for stale params.
        // Cache it (it's still valid for those params) but skip the state
        // commit — the date-range effect will handle the current params once
        // initialLoadComplete is set.
        const startChanged = timeRangeRef.current?.start?.toMillis()
          !== timeRange?.start?.toMillis();
        const finishChanged = timeRangeRef.current?.finish?.toMillis()
          !== timeRange?.finish?.toMillis();
        const paramsStale = startChanged || finishChanged
          || timezoneRef.current !== timezone
          || historianAggregationRef.current !== historianAggregation
          || tradeAggregationRef.current !== tradeAggregation;

        if (!paramsStale) {
          settlementRef.current = { data: mergedData, settledAt: receivedAt };
          setCombinedData(mergedData);
          // Only advance the cache-recovery pointer for data actually committed
          // to state. Advancing it for stale params would let later cache
          // recovery revert to a span the user never successfully viewed.
          lastSuccessfulLoadRef.current = {
            start: timeRange.start, finish: timeRange.finish, cacheKey, timezone,
          };
        } else {
          logger.log('Skipping state commit — params changed during initial load');
        }
        // A successful fetch resolves any in-flight retry indicator.
        resolveRetry();
        initialLoadCompleteRef.current = true;
        setInitialLoadComplete(true);
        if (receivedAt !== null) {
          measureSince('portfolio-show:settlementToStateUpdate:initial', receivedAt);
        }
      },
      onError: (fetchError) => {
        if (cancelToken.current) return;

        // If the user changed timeRange, timezone, or aggregation while the
        // request was in flight, this error belongs to stale params. Ignore it
        // — surfacing it would show the wrong failure and clear data out from
        // under a newer fetch. Mirror the paramsStale guard in onDataReceived.
        // Still flip initialLoadComplete so the date-range effect ungates and
        // fetches the current params; skip skipTimeRangeRef/errorSpanRef so it is
        // free to fetch the new span without treating it as a failed retry.
        const startChanged = timeRangeRef.current?.start?.toMillis()
          !== timeRange?.start?.toMillis();
        const finishChanged = timeRangeRef.current?.finish?.toMillis()
          !== timeRange?.finish?.toMillis();
        const paramsStale = startChanged || finishChanged
          || timezoneRef.current !== timezone
          || historianAggregationRef.current !== historianAggregation
          || tradeAggregationRef.current !== tradeAggregation;

        if (paramsStale) {
          logger.log('Skipping error handling — params changed during initial load');
          initialLoadCompleteRef.current = true;
          setInitialLoadComplete(true);
          return;
        }

        setCombinedData(null);
        handleFetchError(fetchError);
        // Ungate the date-range effect after failure so later date-picker changes can trigger
        //  a fresh fetch.
        // Record the failed span in skipTimeRangeRef to prevent the date-range effect
        //  from auto-retrying it.
        skipTimeRangeRef.current = {
          start: timeRange?.start,
          finish: timeRange?.finish,
        };
        // Remember which span this error belongs to so the date-range effect can
        // tell a genuine date change (clear the error) from the extra same-span
        // re-run it triggers by flipping initialLoadComplete (keep the error).
        errorSpanRef.current = {
          start: timeRange?.start,
          finish: timeRange?.finish,
        };
        initialLoadCompleteRef.current = true;
        setInitialLoadComplete(true);
      },
    });

    return () => {
      cancelToken.current = true;
      if (stateDispatchFunc) {
        stateDispatchFunc({ type: PortfolioShowLoadingActions.CANCEL_REQUEST });
      }
    };
    // retryNonce drives a full-pipeline replay: bumping it re-runs the initial
    // load with the current timeRange closure, matching PropertyShow's retry.
  }, [portfolioId, stateDispatchFunc, retryNonce, handleFetchError, resolveRetry, resetError]);

  // ── Date-range effect: Date-range change (time-series only) ───
  useEffect(() => {
    if (!portfolioId || !timeRange) return undefined;
    // Use the ref (not the state) so this check works even in the same
    // render pass where the initial-load effect reset it for a new portfolioId.
    if (!initialLoadCompleteRef.current) return undefined;

    // Skip when the date-range effect fires for the first time after the
    // initial-load effect completes — it already fetched this timeRange.
    const initialSpan = initialTimeRangeRef.current;
    if (initialSpan
      && timeRange?.start?.toMillis() === initialSpan.start?.toMillis()
      && timeRange?.finish?.toMillis() === initialSpan.finish?.toMillis()) {
      logger.log('Skipping fetch - timeRange already loaded by initial fetch');
      initialTimeRangeRef.current = null;
      return undefined;
    }
    initialTimeRangeRef.current = null;

    // Skip when triggered by cancelFunc or a failure rather than a genuine timeRange change
    const skipTimeRange = skipTimeRangeRef.current;
    skipTimeRangeRef.current = null;

    if (skipTimeRange
      && timeRange?.start?.toMillis() === skipTimeRange.start?.toMillis()
      && timeRange?.finish?.toMillis() === skipTimeRange.finish?.toMillis()) {
      logger.log('Skipping fetch - datespan unchanged since cancellation or failure');
      return undefined;
    }

    logger.log('Effect 2: date-range change', timeRange?.start?.toISO(), '→', timeRange?.finish?.toISO());

    // A genuine date-range change starts a fresh fetch, so discard any stale
    // error from the previous span before it begins. The error is tied to the
    // span that produced it (errorSpanRef); skip the reset when this effect
    // re-runs for that same span — e.g. the extra run triggered when onError
    // flips initialLoadComplete — so a just-surfaced error is not wiped before
    // the user can see it. isRetrying is left untouched (it is resolved once the
    // pipeline settles), mirroring useTradeData.
    const errorSpan = errorSpanRef.current;
    const spanMatchesError = errorSpan
      && timeRange?.start?.toMillis() === errorSpan.start?.toMillis()
      && timeRange?.finish?.toMillis() === errorSpan.finish?.toMillis();
    if (!spanMatchesError) {
      resetError();
      errorSpanRef.current = null;
    }

    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    getPortfolioDataFromCache({
      portfolioId,
      timeRange,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      dispatchFunc: stateDispatchFunc,
      onBeforeFetchFunc: () => {
        // Cache miss — dispatch START_REQUEST before the full pipeline
        // begins. SET_PORTFOLIO_LOADED is dispatched by
        // getPortfolioDataInBatches itself.
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PortfolioShowLoadingActions.START_REQUEST });
        }
      },
      onDataReceivedFunc: (mergedData, fromCache, settledAt) => {
        const receivedAt = settledAt ?? nowMs();
        // Guard: if params changed while a cache-miss fetch was in flight,
        // the data is for a stale range. The cleanup already cancelled the
        // token, but there is a small window between the last token check
        // and this callback. Discard the stale data — the new date-range effect
        // instance will handle the current params.
        const startStale = timeRangeRef.current?.start?.toMillis() !== timeRange?.start?.toMillis();
        const finishStale = timeRangeRef.current?.finish?.toMillis()
          !== timeRange?.finish?.toMillis();
        const dateRangeEffectParamsStale = startStale || finishStale
          || timezoneRef.current !== timezone
          || historianAggregationRef.current !== historianAggregation
          || tradeAggregationRef.current !== tradeAggregation;

        if (dateRangeEffectParamsStale) {
          logger.log('Skipping state commit — date-range effect params changed during fetch');
          return;
        }

        lastSuccessfulLoadRef.current = {
          start: timeRange.start,
          finish: timeRange.finish,
          cacheKey: buildCacheAggregationKey(historianAggregation, tradeAggregation, timezone),
          timezone,
        };

        if (fromCache && stateDispatchFunc) {
          // Cache hit: jump straight to MARK_FULLY_LOADED, mirroring usePropertyData.
          // Blocked by reducer terminal guard during ERROR recovery, so no loading state is
          //  entered.
          const { metersCount, rulesCount } = computeCacheMetadata(mergedData);
          stateDispatchFunc({
            type: PortfolioShowLoadingActions.MARK_FULLY_LOADED,
            metersCount,
            rulesCount,
            batchesLoaded: 1,
            batchesTotal: 1,
          });
        }
        settlementRef.current = { data: mergedData, settledAt: receivedAt };
        setCombinedData(mergedData);
        // A successful fetch (cache hit or miss) resolves the retry indicator.
        resolveRetry();
        if (receivedAt !== null) {
          const label = fromCache
            ? 'portfolio-show:settlementToStateUpdate:cacheHit'
            : 'portfolio-show:settlementToStateUpdate:dateRange';
          measureSince(label, receivedAt);
        }
      },
      onErrorFunc: (fetchError) => {
        if (cancelToken.current) return;
        setCombinedData(null);
        handleFetchError(fetchError);
        // Mirror Effect 1's onError: record the failing span so the next Effect 2
        // run for the same span keeps the error visible (errorSpanRef) and does not
        // auto-retry the same failing fetch (skipTimeRangeRef).
        skipTimeRangeRef.current = { start: timeRange?.start, finish: timeRange?.finish };
        errorSpanRef.current = { start: timeRange?.start, finish: timeRange?.finish };
      },
    });

    return () => {
      cancelToken.current = true;
      if (stateDispatchFunc) {
        stateDispatchFunc({ type: PortfolioShowLoadingActions.CANCEL_REQUEST });
      }
    };
  }, [
    initialLoadComplete,
    portfolioId,
    // Luxon DateTime objects are compared by reference (Object.is) in
    // React dep arrays. Since PortfolioShow rebuilds the timeRange object
    // on each render, the deps appear "changed" even when the date is
    // the same, causing duplicate fetches. Using toMillis() produces a
    // primitive number so the effect only re-runs when the date changes.
    timeRange?.start?.toMillis(),
    timeRange?.finish?.toMillis(),
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    stateDispatchFunc,
    handleFetchError,
    resolveRetry,
    resetError,
  ]);

  const cancelFunc = useCallback(() => {
    cancelTokenRef.current.current = true;
    isFetchingData.current = false;
    skipTimeRangeRef.current = {
      start: timeRangeRef.current?.start,
      finish: timeRangeRef.current?.finish,
    };
    initialLoadCompleteRef.current = true;
    setInitialLoadComplete(true);
    logger.log('Portfolio data fetch cancelled by user');
  }, []);

  // Pure lookup: returns { start, finish, data } for the last successfully loaded
  // cache segment, or null on a miss. Performs no state mutations so it is safe
  // to call from memoised selectors or composed hooks without side effects.
  const getRecoveredSpanFromCache = useCallback(() => {
    const lastLoad = lastSuccessfulLoadRef.current;
    if (!lastLoad) return null;
    const {
      start, finish, cacheKey, timezone: loadTimezone,
    } = lastLoad;
    const currentCache = lookupCache(portfolioId);
    const segment = getSegmentDataFromCache(currentCache.segments, { start, finish }, cacheKey);
    if (!segment) return null;
    const data = extractDataFromCache(segment.data, { start, finish }, loadTimezone);
    return { start, finish, data };
  }, [portfolioId]);

  // Imperative apply: given a recovered span (from getRecoveredSpanFromCache), commits
  // the cached data to state and clears the error and retry indicator.
  // `recovered` may be null/undefined (cache-miss path): in that case
  // `recovered?.data ?? null` evaluates to null, which intentionally clears
  // combinedData so the no-data message is shown rather than stale chart data.
  //
  // The loading reducer is intentionally left in its CANCELLED terminal state.
  // The callers (handleCancel and handleErrorDismiss in index.jsx) call
  // setDatespan({start, finish}) after this, which re-runs the date-range effect;
  // its read-through cache resolves synchronously and dispatches MARK_FULLY_LOADED,
  // which the reducer's terminal guard blocks — so no loading frame is shown.
  const applyRecoveredCacheData = useCallback((recovered) => {
    setCombinedData(recovered?.data ?? null);
    clearError();
    // Clear any in-flight retry indicator so skeletonVisible (dataLoading ||
    // isRetrying) drops to false and the overlay is not forced.
    resolveRetry();
  }, [clearError, resolveRetry]);

  return {
    combinedData,
    cancelFunc,
    clearError,
    error,
    handleRetry,
    isRetrying,
    queryType,
    getRecoveredSpanFromCache,
    applyRecoveredCacheData,
    settlementRef,
  };
};

export default usePortfolioData;
